<script setup lang="ts">
import { type VNode } from 'vue'

import {
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  useForwardProps,
  type DialogRootProps as RadixDialogRootProps
} from 'reka-ui'

import { Box, Divider, Flex, Text, VisuallyHidden } from '@/components'

import FooterActions, { type DialogFooterActionsProps } from './FooterActions.vue'
import FooterContent from './FooterContent.vue'
import FooterRoot from './FooterRoot.vue'
import * as styles from './Root.css'

import { useControlled } from '@/lib/useControlled'

export interface DialogRootProps extends RadixDialogRootProps, DialogFooterActionsProps {
  /* Width of the dialog */

  size?: 'small' | 'medium' | 'large'

  /** Title of the dialog. */

  title: string
}

interface DialogpRootEmits {
  (e: 'update:open', value: boolean): void
}

const { cancelActionLabel, size = 'medium', title, ...rest } = defineProps<DialogRootProps>()
const forward = useForwardProps(rest)
const emit = defineEmits<DialogpRootEmits>()

const { value } = useControlled<boolean | undefined>(() => forward.value.open)

function handleToggle(newValue: boolean) {
  value.value = newValue
  emit('update:open', newValue)
}

const slots = defineSlots<{
  default(props: { close: () => void }): Array<VNode>
  actions(props: { close: () => void }, cancelActionLabel: string | undefined): Array<VNode>
  footerContent(props: { close: () => void }): Array<VNode>
  title(): Array<VNode>
  trigger(): Array<VNode>
}>()
</script>

<template>
  <DialogRoot v-slot="{ close }" v-bind="forward" :open="value" @update:open="handleToggle">
    <DialogTrigger as-child>
      <slot name="trigger" />
    </DialogTrigger>

    <DialogPortal>
      <Box
        as-child
        background-color="tertiary900"
        :class="styles.overlay"
        height="full"
        position="fixed"
        width="full"
      >
        <DialogOverlay />
      </Box>

      <Box
        as-child
        background-color="tertiary50"
        :border-radius="10"
        :class="styles.content({ size })"
        position="fixed"
      >
        <DialogContent :class="styles.content">
          <VisuallyHidden as-child>
            <DialogDescription>{{ title }}</DialogDescription>
          </VisuallyHidden>
          <Flex :class="styles.body" direction="column" :gap="60">
            <Flex
              background-color="tertiary50"
              :class="styles.titleContainer"
              direction="column"
              :gap="60"
              :padding-block-start="100"
              :padding-inline="90"
              position="sticky"
              :z-index="10"
            >
              <Text as-child :size="60" weight="medium">
                <DialogTitle as-child>
                  <h2>
                    <slot name="title">
                      {{ title }}
                    </slot>
                  </h2>
                </DialogTitle>
              </Text>

              <Divider color="tertiary200" orientation="horizontal" />
            </Flex>

            <Box :padding-inline="90">
              <slot :close="close" />
            </Box>

            <Box :padding-inline="90">
              <FooterRoot v-if="!!slots.footerContent || !!slots.actions">
                <FooterContent v-if="!!slots.footerContent">
                  <slot name="footerContent" :close="close" />
                </FooterContent>
                <div v-else />

                <FooterActions v-if="!!slots.actions" :cancel-action-label="cancelActionLabel">
                  <slot name="actions" :close="close" :cancel-action-label="cancelActionLabel" />
                </FooterActions>
              </FooterRoot>
            </Box>
          </Flex>
        </DialogContent>
      </Box>
    </DialogPortal>
  </DialogRoot>
</template>
